
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';

// ==================== Model ====================
export interface TaskChangeRequest {
  id: string;
  taskId: string;
  name: string;
  name2?: string;
  description?: string;
  requestedBy: string;
  requestedDate: string;
  status: string;
  impact?: string;
  ameStatus?: number;
  isAme?: boolean;
  approvedBy?: string;
  approvedDate?: string;
}

// ==================== Async Thunks ====================

// 📥 جلب الطلبات حسب المهمة
export const getTaskChangeRequestsByTaskId = createAsyncThunk(
  'TaskChangeRequest/getByTaskId',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Task/GetTaskChangeRequestByTaskId?taskId=${taskId}`);
      if (data?.succeeded) return data.data;
      return [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ➕ إضافة طلب جديد
export const addTaskChangeRequest = createAsyncThunk(
  'TaskChangeRequest/add',
  async (item: Omit<TaskChangeRequest, 'id' | 'approvedBy' | 'approvedDate'>, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/AddTaskChangeRequest', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في إضافة الطلب');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ✏️ تعديل الطلب
export const updateTaskChangeRequest = createAsyncThunk(
  'TaskChangeRequest/update',
  async (item: TaskChangeRequest, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/UpdateTaskChangeRequest', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في تعديل الطلب');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🗑️ حذف الطلب
export const deleteTaskChangeRequest = createAsyncThunk(
  'TaskChangeRequest/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post(`Task/DeleteTaskChangeRequest?id=${id}`);
      if (data?.succeeded) return id;
      return rejectWithValue(data?.message || 'فشل في حذف الطلب');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==================== Slice ====================

interface TaskChangeRequestState {
  taskChangeRequestList: TaskChangeRequest[];
  loading: boolean;
  error: string | null;
}

const initialState: TaskChangeRequestState = {
  taskChangeRequestList: [],
  loading: false,
  error: null
};

const TaskChangeRequestSlice = createSlice({
  name: 'TaskChangeRequest',
  initialState,
  reducers: {
    clearTaskChangeRequests: state => {
      state.taskChangeRequestList = [];
      state.error = null;
    },
    addLocalTaskChangeRequest: (state, action: PayloadAction<TaskChangeRequest>) => {
      state.taskChangeRequestList.unshift(action.payload);
    },
    updateLocalTaskChangeRequest: (state, action: PayloadAction<TaskChangeRequest>) => {
      const index = state.taskChangeRequestList.findIndex(r => r.id === action.payload.id);
      if (index !== -1) state.taskChangeRequestList[index] = action.payload;
    },
    removeLocalTaskChangeRequest: (state, action: PayloadAction<string>) => {
      state.taskChangeRequestList = state.taskChangeRequestList.filter(r => r.id !== action.payload);
    }
  },
  extraReducers: builder => {
    builder
      // 🔄 Get
      .addCase(getTaskChangeRequestsByTaskId.pending, state => {
        state.loading = true;
      })
      .addCase(getTaskChangeRequestsByTaskId.fulfilled, (state, action) => {
        state.loading = false;
        state.taskChangeRequestList = action.payload;
      })
      .addCase(getTaskChangeRequestsByTaskId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ➕ Add
      .addCase(addTaskChangeRequest.fulfilled, (state, action) => {
        state.taskChangeRequestList.unshift(action.payload);
      })

      // ✏️ Update
      .addCase(updateTaskChangeRequest.fulfilled, (state, action) => {
        const index = state.taskChangeRequestList.findIndex(r => r.id === action.payload.id);
        if (index !== -1) state.taskChangeRequestList[index] = action.payload;
      })

      // 🗑️ Delete
      .addCase(deleteTaskChangeRequest.fulfilled, (state, action) => {
        state.taskChangeRequestList = state.taskChangeRequestList.filter(r => r.id !== action.payload);
      });
  }
});

export const {
  clearTaskChangeRequests,
  addLocalTaskChangeRequest,
  updateLocalTaskChangeRequest,
  removeLocalTaskChangeRequest
} = TaskChangeRequestSlice.actions;

export default TaskChangeRequestSlice.reducer;
